import { useMemo, useState } from 'preact/hooks'
import { canUseMockData } from '@/constants/env'
import { workspaceApi } from '@/api/workspace'
import { campaignApi } from '@/api/campaign'
import { useAnalyticsView } from '@/hooks/useAnalyticsView'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import type { DashboardRange } from '@/types/dashboard'
import type { AttributionView } from '@/types/analytics'
import type { DownloadResult } from '@/api/client'

/** Which surface the visitor analytics are scoped to. */
export type VisitorAnalyticsScope =
  | { scope: 'workspace'; workspaceId: string; campaignId?: never }
  | { scope: 'campaign'; workspaceId: string; campaignId: string }

/**
 * Owns the data/control state for {@link VisitorAnalyticsPanel}: range, attribution view,
 * and mock toggles, plus the scope-aware fetch and CSV export. Keeping the workspace-vs-
 * campaign API branching here lets the panel stay purely presentational. The fetcher is
 * memoized so its identity (and therefore refetching) only changes with the scope/range/
 * mock/attribution inputs.
 */
export function useVisitorAnalytics(target: VisitorAnalyticsScope) {
  const [range, setRange] = useState<DashboardRange>('Days30')
  const [attributionView, setAttributionView] = useState<AttributionView>('ConversionSession')
  const [useMock, setUseMock] = useState(canUseMockData())
  const { canExport } = useWorkspaceCapabilities()

  const workspaceId = target.workspaceId
  const campaignId = target.scope === 'campaign' ? target.campaignId : undefined

  const analyticsFetcher = useMemo(
    () => {
      if (!workspaceId) return null
      if (target.scope === 'campaign') {
        return campaignId ? () => campaignApi.getAnalytics(workspaceId, campaignId, range, useMock, attributionView) : null
      }
      return () => workspaceApi.getAnalytics(workspaceId, range, useMock, attributionView)
    },
    [target.scope, workspaceId, campaignId, range, useMock, attributionView],
  )
  const { data: analytics, loading, error } = useAnalyticsView(analyticsFetcher)

  const exportAnalytics = (): Promise<DownloadResult> =>
    target.scope === 'campaign'
      ? campaignApi.exportAnalytics(target.workspaceId, target.campaignId, range, useMock, attributionView)
      : workspaceApi.exportAnalytics(target.workspaceId, range, useMock, attributionView)

  const exportFilename =
    target.scope === 'campaign'
      ? `visitor-export-campaign-${campaignId}-${range}-${attributionView.toLowerCase()}.csv`
      : `visitor-export-workspace-${workspaceId}-${range}-${attributionView.toLowerCase()}.csv`

  return {
    analytics, loading, error,
    range, setRange,
    attributionView, setAttributionView,
    useMock, setUseMock,
    showMockToggle: canUseMockData(),
    canExport,
    exportAnalytics,
    exportFilename,
  }
}
